var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { localized, msg } from '@lit/localize';
import '../card/BasicCard.js';
const DEFAULT_COLORS = { border: '#2196F3', bg: '#E3F2FD' };
const VARIANT_COLORS = {
    info: DEFAULT_COLORS,
    success: { border: '#4CAF50', bg: '#E8F5E9' },
    warning: { border: '#FF9800', bg: '#FFF3E0' },
    error: { border: '#F44336', bg: '#FFEBEE' },
};
const DEFAULT_ICON = '\u2139\uFE0F';
const VARIANT_ICONS = {
    info: DEFAULT_ICON,
    success: '\u2705',
    warning: '\u26A0\uFE0F',
    error: '\u274C',
};
let NotificationPopup = class NotificationPopup extends LitElement {
    get variant() {
        return this.notification?.style?.type ?? 'info';
    }
    get colors() {
        return VARIANT_COLORS[this.variant] ?? DEFAULT_COLORS;
    }
    get variantIcon() {
        return VARIANT_ICONS[this.variant] ?? DEFAULT_ICON;
    }
    dismiss() {
        this.remove();
    }
    handleClick() {
        this.dispatchEvent(new CustomEvent('notification-popup-click', {
            detail: this.notification,
            bubbles: true,
            composed: true,
        }));
        this.dismiss();
    }
    handleLinkClick(e) {
        e.stopPropagation();
        this.dispatchEvent(new CustomEvent('notification-link-click', {
            detail: { notification: this.notification, link: this.notification.link },
            bubbles: true,
            composed: true,
        }));
        this.dismiss();
    }
    handleClose(e) {
        e.stopPropagation();
        this.dismiss();
    }
    getLinkLabel() {
        const link = this.notification.link;
        if (!link)
            return '';
        if (link.type === 'map')
            return msg('Open map', { id: 'notification.link.openMap' });
        if (link.type === 'module')
            return msg('Open module', { id: 'notification.link.openModule' });
        if (link.type === 'url')
            return msg('Open link', { id: 'notification.link.openUrl' });
        return msg('Open', { id: 'notification.open' });
    }
    render() {
        if (!this.notification)
            return nothing;
        const { colors } = this;
        return html `
      <basic-card
        size="small"
        style="--popup-border-color: ${colors.border}; border-left-color: ${colors.border};"
      >
        <div class="notification-popup__body">
          <span class="notification-popup__icon">${this.variantIcon}</span>

          <div class="notification-popup__content" @click="${this.handleClick}">
            <h4 class="notification-popup__title">${this.notification.title}</h4>
            ${this.notification.description
            ? html `<p class="notification-popup__description">${this.notification.description}</p>`
            : nothing}

            ${this.notification.link
            ? html `<span class="notification-popup__link" @click="${this.handleLinkClick}">${this.getLinkLabel()} &rarr;</span>`
            : nothing}
          </div>

          <button
            class="notification-popup__close"
            @click="${this.handleClose}"
            title="${msg('Close', { id: 'notification.popup.close' })}"
          >&times;</button>
        </div>
      </basic-card>
    `;
    }
};
NotificationPopup.styles = css `
    :host {
      display: block;
      position: fixed;
      top: 16px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 9999;
      pointer-events: auto;
    }

    basic-card {
      border-left: 4px solid var(--popup-border-color, #2196F3);
      animation: slide-in 0.3s ease-out;
      min-width: 300px;
      max-width: 420px;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    }

    @keyframes slide-in {
      from {
        transform: translateY(-100%);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }

    .notification-popup__body {
      display: flex;
      align-items: flex-start;
      gap: 10px;
    }

    .notification-popup__icon {
      font-size: 1.25rem;
      flex-shrink: 0;
      margin-top: 1px;
    }

    .notification-popup__content {
      flex: 1;
      min-width: 0;
    }

    .notification-popup__title {
      margin: 0;
      font-size: 0.875rem;
      font-weight: 600;
      color: #333;
    }

    .notification-popup__description {
      margin: 4px 0 0;
      font-size: 0.8rem;
      color: #666;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .notification-popup__close {
      background: none;
      border: none;
      font-size: 1rem;
      color: #999;
      cursor: pointer;
      padding: 0;
      line-height: 1;
      flex-shrink: 0;
    }

    .notification-popup__close:hover {
      color: #333;
    }

    .notification-popup__link {
      display: inline-block;
      margin-top: 6px;
      font-size: 0.75rem;
      color: #1976D2;
      cursor: pointer;
      font-weight: 500;
    }

    .notification-popup__link:hover {
      text-decoration: underline;
    }
  `;
__decorate([
    property({ type: Object })
], NotificationPopup.prototype, "notification", void 0);
NotificationPopup = __decorate([
    localized(),
    customElement('notification-popup')
], NotificationPopup);
export { NotificationPopup };
//# sourceMappingURL=NotificationPopup.js.map